import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { Moment } from 'moment'
import {
  FaFilter,
  FaCheckCircle,
  FaClone,
  FaFileDownload,
} from 'react-icons/fa'
import { FaCircleXmark } from 'react-icons/fa6'
import { CSVLink } from 'react-csv'
import { colors } from '../../../../../constants'
import {
  Account,
  GOClientLocation,
  GOClientSettings,
  GOScheduleMenu,
  SchedulePlannerMenuItem,
  ShowInfoModalReq,
} from '@types'
import { parseMilitaryTime } from '../../../../../utils'
import { FlexContainer, Panel, XSpacing, YSpacing } from '@components/common'
import { Button, DateTimeInput } from '@components/common/form'
import { MultiSelectDropDown } from '@components/common/form'
import LoadingIndicator from '@components/common/LoadingIndicator'
import { useGOMenuSearch, Filters } from './useGOMenuSearch'
import { menusToCSV } from '../menuSuggestionsHelpers'
import ClientLocationSelector, {
  SearchClientSettingsParams,
} from '../ClientLocationSelector'
import {
  SearchGOMenusResponse,
  GroupedMenus,
} from '@coordinators/groupOrders/menus'

export const formatCutoffTime = (days: number, time: number) => {
  let out = ''
  switch (days) {
    case 1:
      out = 'One Day Before'
      break
    case 2:
      out = 'Two Days Before'
      break
    default:
      out = 'Same Day'
  }

  const res = parseMilitaryTime(time)
  const minutes = res[1]
  let hours = res[0]
  if (!hours && !minutes) {
    return out
  }
  const amOrPM = hours > 12 ? 'PM' : 'AM'
  hours = hours > 12 ? hours - 12 : hours
  const cutoffTime = `${hours}${!minutes ? ':00' : `:${minutes}`} ${amOrPM}`

  out += ` at ${cutoffTime}`

  return out
}

export const formatNumberToTime = (time: number | null) => {
  if (!time || time < 100) {
    return ''
  }

  return time.toString().slice(0, -2) + ':' + time.toString().slice(-2)
}

const tableHeadings = [
  'Menu Card Name',
  'Vendor',
  'Menu Card Cutoff Time',
  'Menu Items',
  'Cuisine(s)',
  'Ready to Heat',
  'Avg Menu Item Price',
  'Avg Menu Margin',
  'Vendor Pickup Time (On Date)',
  'Vendor Earliest Pickup Time',
  'Vendor Max Items / Day',
  'Vendor Menucard Cap',
  'Client Name',
  'Last Ordered by Client',
  'Distance (miles) from Client',
  'Avg Client Menu Item Rating',
]

// Define sorting field mappings
const sortFieldMap: Record<string, string> = {
  'Menu Card Name': 'name',
  Vendor: 'chefName',
  'Menu Card Cutoff Time': 'groupOrderCutoffTime',
  'Menu Items': 'allMenuItems',
  'Cuisine(s)': 'cuisineTags',
  'Ready to Heat': 'readyToHeat',
  'Avg Menu Item Price': 'avgPrice',
  'Avg Menu Margin': 'margin',
  'Vendor Pickup Time (On Date)': 'pickupTime',
  'Vendor Max Items / Day': 'vendorMaxGOItems',
  'Vendor Menucard Cap': 'menuMaxItems',
  'Client Name': 'clientName',
  'Last Ordered by Client': 'lastOrdered',
  'Distance (miles) from Client': 'distance',
  'Avg Client Menu Item Rating': 'avgMenuItemRatingByClient',
}

interface FilterProps {
  isVisible?: boolean
  margin?: string
  onClick?: () => void
}

interface GroupOrderMenusFinderProps {
  hq: number
  client: Account | null
  clientSettings: GOClientSettings | null
  withinBudget: boolean | null
  latitude?: number
  longitude?: number
  availMenus: GOScheduleMenu[]
  savedFilters: Filters | null
  saveFilters: (filters: Filters) => void
  searchGOMenus: (val: any) => Promise<SearchGOMenusResponse | null>
  setAvailMenus: (menus: GOScheduleMenu[]) => void
  loadCuisines: () => Promise<any[]>
  loadAccounts: (val: { search: string }) => Promise<Account[]>
  searchClientSettings: (
    params: SearchClientSettingsParams,
  ) => Promise<GOClientSettings[]>
  onSelectClientLocation: (
    account: Account,
    setting: GOClientSettings,
    location: GOClientLocation,
  ) => void
  onChangeWithinBudget: (withinBudget: boolean) => void
  displayFailureMessage: (mssg: string) => void
  displayInfoMessage: (mssg: string) => void
  showInfoModal: (req: ShowInfoModalReq) => void
}

// Define a new component for displaying grouped menus
const GroupedMenusButton = ({
  groupedMenus,
  showInfoModal,
}: {
  groupedMenus: GroupedMenus | null
  showInfoModal: (req: ShowInfoModalReq) => void
}) => {
  if (!groupedMenus || Object.keys(groupedMenus).length === 0) {
    return null
  }

  const handleShowGroupedMenus = () => {
    // Create content for the modal
    const content = (
      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <h3>Duplicates removed</h3>
        <p>
          The following menus were identified as client specific duplicates and
          only one from each group was included in the results:
        </p>
        <YSpacing height="20px" />

        {Object.values(groupedMenus).map((menuGroups) => {
          return Object.entries(menuGroups).map(
            ([parentMenuName, duplicates]) => {
              const firstDuplicate = duplicates[0]

              return (
                <div
                  key={parentMenuName}
                  style={{
                    marginBottom: '20px',
                    padding: '10px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                  }}
                >
                  <h4
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      paddingBottom: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <strong>{parentMenuName}</strong> (Included)
                  </h4>
                  <p>Vendor: {firstDuplicate.chef.name}</p>
                  <p>Menus excluded:</p>
                  <ul style={{ paddingLeft: '20px' }}>
                    {duplicates.map((menu) => (
                      <li key={menu.id} style={{ marginBottom: '8px' }}>
                        <strong>{menu.name}</strong>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {menu.cuisineTags.join(', ')}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            },
          )
        })}
      </div>
    )

    showInfoModal({
      title: 'Duplicate Menus Removed',
      content,
    })
  }

  // Count total duplicates across all groups
  const totalDuplicates = Object.values(groupedMenus).reduce(
    (outerTotal, menuGroups) => {
      return (
        outerTotal +
        Object.values(menuGroups).reduce(
          (innerTotal, group) => innerTotal + group.length - 1,
          0,
        )
      )
    },
    0,
  )

  return (
    <GroupedMenusButtonStyled onClick={handleShowGroupedMenus}>
      <FaClone size={14} style={{ marginRight: '5px' }} />
      {totalDuplicates} Duplicate Menu{totalDuplicates !== 1 ? 's' : ''}{' '}
      Excluded
    </GroupedMenusButtonStyled>
  )
}

const GroupOrderMenusFinder = ({
  hq,
  client,
  clientSettings,
  withinBudget,
  latitude,
  longitude,
  availMenus,
  savedFilters,
  saveFilters,
  searchGOMenus,
  setAvailMenus,
  loadCuisines,
  loadAccounts,
  searchClientSettings,
  onSelectClientLocation,
  onChangeWithinBudget,
  displayFailureMessage,
  displayInfoMessage,
  showInfoModal,
}: GroupOrderMenusFinderProps) => {
  const [expanded, updateExpanded] = useState<Record<string, boolean>>({})
  const [showFilter, setShowFilter] = useState(true)
  const [visibleColumns, setVisibleColumns] = useState<string[]>([])

  // Add state for import/export of filters
  const [showImportExport, setShowImportExport] = useState(false)
  const [importFilterText, setImportFilterText] = useState('')
  const [importError, setImportError] = useState<string | null>(null)

  const {
    menus,
    cuisineOptions,
    chefOptions,
    menuOptions,
    availOn,
    totalMenus,
    loadingMenus,
    readyToSearch,
    filters,
    groupedMenus,
    setAvailOn,
    setCuisines,
    setReadyToHeat,
    setAvgMenuPriceGT,
    setAvgMenuPriceLT,
    setAvgMenuMarginGT,
    setAvgMenuMarginLT,
    setVendorMaxItemsGT,
    setVendorMaxItemsLT,
    setMenuOrderingCapGT,
    setMenuOrderingCapLT,
    setDistanceLT,
    setLastScheduledGT,
    setAvgRating,
    setMenucardCutoffs,
    setExcludeChefIds,
    setExcludeMenuIds,
    setExcludeClientSpecificMenus,
    sortMenus,
    sortByIcon,
    executeSearchGOMenus,
    resetSearch,
    setAllFilters,
    setMenus,
    excludeMenuAndUpdate,
  } = useGOMenuSearch({
    displayFailureMessage,
    searchGOMenus,
    loadCuisines,
    hqId: hq,
    clientId: client?.id,
    clientName: client?.name,
    clientSettingsId: clientSettings?.id,
    latitude,
    longitude,
    withinBudget: withinBudget ?? undefined,
  })

  const {
    cuisines,
    readyToHeat,
    avgMenuPriceGT,
    avgMenuPriceLT,
    avgMenuMarginGT,
    avgMenuMarginLT,
    vendorMaxItemsGT,
    vendorMaxItemsLT,
    menuOrderingCapGT,
    menuOrderingCapLT,
    distanceLT,
    lastScheduledGT,
    avgRating,
    menucardCutoffs,
    excludeChefIds,
    excludeMenuIds,
    excludeClientSpecificMenus,
  } = filters

  useEffect(() => {
    if (savedFilters) {
      setAllFilters(savedFilters)
    }
  }, [savedFilters, setAllFilters])

  // Load the available menus into the custom hook state
  useEffect(() => {
    if (availMenus.length > 0 && menus.length === 0) {
      setMenus(availMenus)
    }
  }, [availMenus, setMenus, menus])

  // Prepare CSV data
  const csvData = menusToCSV(menus)
  const csvFilename = `menus-${new Date().toISOString().split('T')[0]}.csv`

  // Initialize visible columns
  useEffect(() => {
    if (visibleColumns.length === 0) {
      setVisibleColumns(tableHeadings)
    }
  }, [visibleColumns.length])

  // Check if a column is visible
  const isColumnVisible = (columnName: string) => {
    return visibleColumns.includes(columnName)
  }

  // Toggle column visibility
  const handleToggleColumnVisibility = (column: string) => {
    if (visibleColumns.includes(column)) {
      // Hide the column
      setVisibleColumns(visibleColumns.filter((col) => col !== column))
    } else {
      // Show the column
      setVisibleColumns([...visibleColumns, column])
    }
  }

  // Handle sort column click
  const handleSort = (columnName: string) => {
    const sortField = sortFieldMap[columnName]
    if (sortField) {
      const transform =
        sortField === 'name' ? (name: string) => name.toLowerCase() : undefined
      sortMenus(sortField as keyof GOScheduleMenu, transform)
    }
  }

  // Handle menu item name rendering
  const renderMenuItemNames = (
    items: SchedulePlannerMenuItem[] = [],
    menuId: string,
  ) => {
    const names = items.map((it) => it.name).join(', ')

    const needExpand = names.length > 45

    const renderedNames = needExpand
      ? expanded[menuId]
        ? names
        : names.slice(0, 45).concat('...')
      : names

    return (
      <td className="menu-item-cell">
        <p>{renderedNames}</p>
        {needExpand && (
          <Expand
            expanded={expanded[menuId]}
            onClick={() =>
              updateExpanded({ ...expanded, [menuId]: !expanded[menuId] })
            }
          >
            {expanded[menuId] ? 'Close' : 'Expand'}
          </Expand>
        )}
      </td>
    )
  }

  // Handle cutoff time rendering
  const renderCutoffTime = (days: number, time: number) => {
    const out = formatCutoffTime(days, time)

    return <td>{out}</td>
  }

  // Function to export filters to JSON
  const exportFilters = () => {
    // Convert to JSON and copy to clipboard
    const jsonString = JSON.stringify(filters, null, 2)
    navigator.clipboard
      .writeText(jsonString)
      .then(() => {
        alert('Filters copied to clipboard!')
      })
      .catch((_) => {
        displayFailureMessage('Failed to copy filters to clipboard')
      })
  }

  // Function to import filters from JSON
  const importFilters = () => {
    try {
      setImportError(null)
      const importedFilters = JSON.parse(importFilterText) as Filters

      // Apply the imported filters using the setAllFilters function
      setAllFilters(importedFilters)
      saveFilters(importedFilters)

      // Clear the import text and hide the import/export section
      setImportFilterText('')
      setShowImportExport(false)

      displayInfoMessage('Filters imported successfully!')
    } catch (error) {
      setImportError('Invalid JSON format. Please check your input.')
    }
  }

  // Add a new function to render menu name with exclude button
  const renderMenuNameWithExcludeButton = (menu: GOScheduleMenu) => (
    <td className="menu-name-cell">
      <FlexContainer
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <span>{menu.name}</span>
        <ExcludeButton
          title="Exclude this menu"
          onClick={(e) => {
            e.stopPropagation() // Prevent row clicks if any
            excludeMenuAndUpdate(menu.id)
            displayInfoMessage(`Excluded ${menu.name} from results`)
          }}
        >
          <FaCircleXmark size={16} />
        </ExcludeButton>
      </FlexContainer>
    </td>
  )

  return (
    <SchedulePlannerBody>
      <FlexContainer flexDirection="column">
        <FlexContainer flexDirection="row" justifyContent="space-between">
          <FilterToggle
            isVisible={showFilter}
            onClick={() => setShowFilter(!showFilter)}
          >
            <FaFilter />
            {showFilter ? 'Hide Filters' : 'Show Filters'}
          </FilterToggle>
        </FlexContainer>
        <YSpacing height="10px" />
        <FlexContainer flexDirection="row" justifyContent="flex-start">
          {showFilter && (
            <FiltersPanel width={'100%'} minWidth="300px" maxWidth={'350px'}>
              {/* Add Export/Import Section */}
              <YSpacing height="20px" />
              <Button
                label={
                  showImportExport
                    ? 'Hide Export/Import'
                    : 'Export/Import Filters'
                }
                width="100%"
                onClick={() => setShowImportExport(!showImportExport)}
              />
              <YSpacing height="10px" />
              {showImportExport && (
                <FiltersSection>
                  <SectionHeader>Export/Import Filters</SectionHeader>
                  <YSpacing height="10px" />

                  {/* Export button */}
                  <Button
                    label="Copy Filters to Clipboard"
                    width="100%"
                    onClick={exportFilters}
                  />

                  <YSpacing height="20px" />
                  <Divider />
                  <YSpacing height="20px" />

                  {/* Import section */}
                  <SectionHeader>Import Filters</SectionHeader>
                  <YSpacing height="5px" />
                  <textarea
                    placeholder="Paste JSON filter configuration here..."
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '8px',
                      border: importError ? '1px solid red' : '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                    }}
                    value={importFilterText}
                    onChange={(e) => {
                      setImportFilterText(e.target.value)
                      if (importError) {
                        setImportError(null)
                      }
                    }}
                  />

                  {importError && (
                    <div
                      style={{
                        color: 'red',
                        fontSize: '12px',
                        marginTop: '5px',
                      }}
                    >
                      {importError}
                    </div>
                  )}

                  <YSpacing height="10px" />
                  <Button
                    label="Import Filters"
                    width="100%"
                    onClick={importFilters}
                    disabled={!importFilterText.trim()}
                  />
                </FiltersSection>
              )}

              <YSpacing height="10px" />
              {menus.length > 0 && (
                <CSVLink
                  data={csvData}
                  filename={csvFilename}
                  style={{
                    textDecoration: 'none',
                    width: '100%',
                    justifyItems: 'center',
                  }}
                  target="_blank"
                >
                  <ExportButton>
                    <FaFileDownload />
                    Export to CSV
                  </ExportButton>
                  <YSpacing height="20px" />
                </CSVLink>
              )}

              <Button
                label="Search"
                width="100%"
                onClick={async () => {
                  const foundMenus = await executeSearchGOMenus()
                  if (foundMenus) {
                    setAvailMenus(foundMenus)
                  }
                }}
                disabled={!readyToSearch || loadingMenus}
              />

              {groupedMenus && Object.keys(groupedMenus).length > 0 && (
                <>
                  <YSpacing height="20px" />
                  <GroupedMenusButton
                    groupedMenus={groupedMenus}
                    showInfoModal={showInfoModal}
                  />
                </>
              )}

              <YSpacing height="20px" />

              {/* Column Visibility Filter */}
              <SidebarSectionTitle>Column Visibility</SidebarSectionTitle>
              <YSpacing height="5px" />
              <SelectionContainer>
                {tableHeadings.map((column) => (
                  <CheckboxContainer key={column}>
                    <input
                      type="checkbox"
                      id={`column-${column}`}
                      checked={visibleColumns.includes(column)}
                      onChange={() => handleToggleColumnVisibility(column)}
                    />
                    <label htmlFor={`column-${column}`}>{column}</label>
                  </CheckboxContainer>
                ))}
              </SelectionContainer>
              <YSpacing height="20px" />
              <Divider />
              <YSpacing height="10px" />

              <SidebarSectionTitle>Client (Optional)</SidebarSectionTitle>
              <YSpacing height="20px" />

              <FiltersSection>
                <ClientLocationSelector
                  onSelect={onSelectClientLocation}
                  loadAccounts={loadAccounts}
                  searchClientSettings={searchClientSettings}
                  initialAccount={client}
                  initialClientSetting={clientSettings}
                  withinBudget={withinBudget}
                  onChangeWithinBudget={onChangeWithinBudget}
                />
                <YSpacing height="10px" />

                {/* Distance Filter */}
                <SectionHeader>Max Distance To Client</SectionHeader>
                <YSpacing height="5px" />
                <FlexContainer flexDirection="column" width="100%">
                  <label
                    htmlFor="max-distance"
                    style={{ fontSize: '12px', marginBottom: '4px' }}
                  >
                    Max Distance (miles)
                  </label>
                  <input
                    id="max-distance"
                    type="number"
                    min="0"
                    step="0.1"
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                    }}
                    value={distanceLT === null ? '' : distanceLT}
                    onChange={(e) => {
                      const value = e.target.value.trim()
                      setDistanceLT(value === '' ? null : Number(value))
                    }}
                    placeholder="Enter maximum distance"
                  />
                </FlexContainer>
              </FiltersSection>

              <SidebarSectionTitle>Filters</SidebarSectionTitle>
              <YSpacing height="20px" />

              <FiltersSection>
                {/* Exclude client specific menus */}
                <YSpacing height="5px" />
                <FlexContainer flexDirection="row" alignItems="center">
                  <input
                    type="checkbox"
                    id="exclude-client-specific-menus"
                    checked={excludeClientSpecificMenus}
                    onChange={() =>
                      setExcludeClientSpecificMenus(!excludeClientSpecificMenus)
                    }
                  />
                  <XSpacing width="10px" />
                  <CheckboxLabel>Exclude client specific menus</CheckboxLabel>
                </FlexContainer>
                <YSpacing height="20px" />

                {/* Date/Time Filter */}
                <YSpacing height="15px" />
                <DateTimeInput
                  width="100%"
                  label="Available On"
                  date={availOn}
                  onChange={(date: Moment) => setAvailOn(date)}
                  clearDate={() => setAvailOn(null)}
                />
                <YSpacing height="20px" />

                {/* Cuisine Filter */}
                <SectionHeader>Cuisines</SectionHeader>
                <YSpacing height="5px" />
                <MultiSelectDropDown
                  width="100%"
                  label=""
                  includeSelectAll={true}
                  options={cuisineOptions}
                  checkedOptions={cuisines || []}
                  renderCheckedOptions={true}
                  searchPlaceholder="Search cuisines..."
                  onChange={(selected: string[]) => {
                    setCuisines(selected.length > 0 ? selected : null)
                  }}
                />
                <YSpacing height="20px" />

                {/* Ready to Heat Filter */}
                <YSpacing height="5px" />
                <FlexContainer flexDirection="column" width="100%">
                  <SectionHeader>Ready To Heat</SectionHeader>
                  <select
                    id="ready-to-heat"
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                    }}
                    value={
                      readyToHeat === null ? 'all' : readyToHeat ? 'yes' : 'no'
                    }
                    onChange={(e) => {
                      const { value } = e.target
                      if (value === 'all') {
                        setReadyToHeat(null)
                      } else if (value === 'yes') {
                        setReadyToHeat(true)
                      } else {
                        setReadyToHeat(false)
                      }
                    }}
                  >
                    <option value="all">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </FlexContainer>
                <YSpacing height="20px" />

                {/* Price Range Filters */}
                <SectionHeader>Menu Price Range</SectionHeader>
                <YSpacing height="5px" />
                <FlexContainer
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <FlexContainer flexDirection="column" width="48%">
                    <label
                      htmlFor="min-price"
                      style={{ fontSize: '12px', marginBottom: '4px' }}
                    >
                      Min Price ($)
                    </label>
                    <input
                      id="min-price"
                      type="number"
                      min="0"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      value={avgMenuPriceGT === null ? '' : avgMenuPriceGT}
                      onChange={(e) => {
                        const value = e.target.value.trim()
                        setAvgMenuPriceGT(value === '' ? null : Number(value))
                      }}
                      placeholder="Min $"
                    />
                  </FlexContainer>
                  <FlexContainer flexDirection="column" width="48%">
                    <label
                      htmlFor="max-price"
                      style={{ fontSize: '12px', marginBottom: '4px' }}
                    >
                      Max Price ($)
                    </label>
                    <input
                      id="max-price"
                      type="number"
                      min="0"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      value={avgMenuPriceLT === null ? '' : avgMenuPriceLT}
                      onChange={(e) => {
                        const value = e.target.value.trim()
                        setAvgMenuPriceLT(value === '' ? null : Number(value))
                      }}
                      placeholder="Max $"
                    />
                  </FlexContainer>
                </FlexContainer>
                <YSpacing height="20px" />

                {/* Menu Margin Range Filters */}
                <SectionHeader>Menu Margin Range</SectionHeader>
                <YSpacing height="5px" />
                <FlexContainer
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <FlexContainer flexDirection="column" width="48%">
                    <label
                      htmlFor="min-margin"
                      style={{ fontSize: '12px', marginBottom: '4px' }}
                    >
                      Min Margin (%)
                    </label>
                    <input
                      id="min-margin"
                      type="number"
                      min="0"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      value={avgMenuMarginGT === null ? '' : avgMenuMarginGT}
                      onChange={(e) => {
                        const value = e.target.value.trim()
                        setAvgMenuMarginGT(value === '' ? null : Number(value))
                      }}
                      placeholder="Min %"
                    />
                  </FlexContainer>
                  <FlexContainer flexDirection="column" width="48%">
                    <label
                      htmlFor="max-margin"
                      style={{ fontSize: '12px', marginBottom: '4px' }}
                    >
                      Max Margin (%)
                    </label>
                    <input
                      id="max-margin"
                      type="number"
                      min="0"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      value={avgMenuMarginLT === null ? '' : avgMenuMarginLT}
                      onChange={(e) => {
                        const value = e.target.value.trim()
                        setAvgMenuMarginLT(value === '' ? null : Number(value))
                      }}
                      placeholder="Max %"
                    />
                  </FlexContainer>
                </FlexContainer>
                <YSpacing height="20px" />

                {/* Vendor Max Items Range Filters */}
                <SectionHeader>Vendor Max Items Range</SectionHeader>
                <YSpacing height="5px" />
                <FlexContainer
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <FlexContainer flexDirection="column" width="48%">
                    <label
                      htmlFor="min-items"
                      style={{ fontSize: '12px', marginBottom: '4px' }}
                    >
                      Min Items
                    </label>
                    <input
                      id="min-items"
                      type="number"
                      min="0"
                      step="1"
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      value={vendorMaxItemsGT === null ? '' : vendorMaxItemsGT}
                      onChange={(e) => {
                        const value = e.target.value.trim()
                        setVendorMaxItemsGT(value === '' ? null : Number(value))
                      }}
                      placeholder="Min items"
                    />
                  </FlexContainer>
                  <FlexContainer flexDirection="column" width="48%">
                    <label
                      htmlFor="max-items"
                      style={{ fontSize: '12px', marginBottom: '4px' }}
                    >
                      Max Items
                    </label>
                    <input
                      id="max-items"
                      type="number"
                      min="0"
                      step="1"
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      value={vendorMaxItemsLT === null ? '' : vendorMaxItemsLT}
                      onChange={(e) => {
                        const value = e.target.value.trim()
                        setVendorMaxItemsLT(value === '' ? null : Number(value))
                      }}
                      placeholder="Max items"
                    />
                  </FlexContainer>
                </FlexContainer>
                <YSpacing height="20px" />

                {/* Menu Ordering Cap Range Filters */}
                <SectionHeader>Menu Ordering Cap Range</SectionHeader>
                <YSpacing height="5px" />
                <FlexContainer
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <FlexContainer flexDirection="column" width="48%">
                    <label
                      htmlFor="min-menu-cap"
                      style={{ fontSize: '12px', marginBottom: '4px' }}
                    >
                      Min Menu Cap
                    </label>
                    <input
                      id="min-menu-cap"
                      type="number"
                      min="0"
                      step="1"
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      value={
                        menuOrderingCapGT === null ? '' : menuOrderingCapGT
                      }
                      onChange={(e) => {
                        const value = e.target.value.trim()
                        setMenuOrderingCapGT(
                          value === '' ? null : Number(value),
                        )
                      }}
                      placeholder="Min cap"
                    />
                  </FlexContainer>
                  <FlexContainer flexDirection="column" width="48%">
                    <label
                      htmlFor="max-menu-cap"
                      style={{ fontSize: '12px', marginBottom: '4px' }}
                    >
                      Max Menu Cap
                    </label>
                    <input
                      id="max-menu-cap"
                      type="number"
                      min="0"
                      step="1"
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      value={
                        menuOrderingCapLT === null ? '' : menuOrderingCapLT
                      }
                      onChange={(e) => {
                        const value = e.target.value.trim()
                        setMenuOrderingCapLT(
                          value === '' ? null : Number(value),
                        )
                      }}
                      placeholder="Max cap"
                    />
                  </FlexContainer>
                </FlexContainer>
                <YSpacing height="20px" />

                {/* Last Scheduled Filter */}
                <SectionHeader>Min Weeks Last Scheduled</SectionHeader>
                <YSpacing height="5px" />
                <FlexContainer flexDirection="column" width="100%">
                  <label
                    htmlFor="min-weeks"
                    style={{ fontSize: '12px', marginBottom: '4px' }}
                  >
                    Minimum Weeks
                  </label>
                  <input
                    id="min-weeks"
                    type="number"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                    }}
                    value={lastScheduledGT === null ? '' : lastScheduledGT}
                    onChange={(e) => {
                      const value = e.target.value.trim()
                      setLastScheduledGT(value === '' ? null : Number(value))
                    }}
                    placeholder="Enter minimum weeks"
                  />
                  <div
                    style={{
                      fontSize: '11px',
                      marginTop: '4px',
                      color: '#666',
                    }}
                  >
                    Filter for menus not scheduled in at least this many weeks
                  </div>
                </FlexContainer>
                <YSpacing height="20px" />

                {/* Rating Filter */}
                <SectionHeader>Min Rating</SectionHeader>
                <YSpacing height="5px" />
                <FlexContainer flexDirection="column" width="100%">
                  <label
                    htmlFor="min-rating"
                    style={{ fontSize: '12px', marginBottom: '4px' }}
                  >
                    Minimum Rating (1-5)
                  </label>
                  <input
                    id="min-rating"
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    style={{
                      width: '100%',
                      padding: '6px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                    }}
                    value={avgRating === null ? '' : avgRating}
                    onChange={(e) => {
                      const value = e.target.value.trim()
                      setAvgRating(value === '' ? null : Number(value))
                    }}
                    placeholder="Enter minimum rating"
                  />
                  <div
                    style={{
                      fontSize: '11px',
                      marginTop: '4px',
                      color: '#666',
                    }}
                  >
                    Minimum average rating by client
                  </div>
                </FlexContainer>
                <YSpacing height="20px" />

                {/* Cutoffs Filter */}
                <SectionHeader>Cutoff Times</SectionHeader>
                <YSpacing height="5px" />
                <MultiSelectDropDown
                  width="100%"
                  label=""
                  includeSelectAll={true}
                  options={[
                    { value: '0', label: 'Same Day Cutoff' },
                    { value: '1', label: '1 Day Cutoff' },
                    { value: '2', label: '2 Day Cutoff' },
                  ]}
                  checkedOptions={
                    menucardCutoffs ? menucardCutoffs.map(String) : []
                  }
                  renderCheckedOptions={true}
                  searchPlaceholder="Search cutoff options..."
                  onChange={(selected: string[]) => {
                    setMenucardCutoffs(
                      selected.length > 0 ? selected.map(Number) : null,
                    )
                  }}
                />
                <div
                  style={{ fontSize: '11px', marginTop: '4px', color: '#666' }}
                >
                  Filter by menu cutoff day requirements
                </div>
                <YSpacing height="20px" />

                {/* Exclude Chef IDs Filter */}
                <SectionHeader>Exclude Chefs</SectionHeader>
                <YSpacing height="5px" />
                <MultiSelectDropDown
                  width="100%"
                  label=""
                  includeSelectAll={true}
                  options={chefOptions}
                  checkedOptions={excludeChefIds || []}
                  renderCheckedOptions={true}
                  renderCheckedOptionsLabel={true}
                  searchPlaceholder="Search chefs..."
                  onChange={(selected: string[]) => {
                    setExcludeChefIds(selected.length > 0 ? selected : null)
                  }}
                />
                <div
                  style={{ fontSize: '11px', marginTop: '4px', color: '#666' }}
                >
                  Select vendors to exclude from search results
                </div>
                <YSpacing height="20px" />

                {/* Exclude Menu IDs Filter - only show if menus are loaded */}
                {menus.length > 0 ? (
                  <>
                    <SectionHeader>Exclude Menu Cards</SectionHeader>
                    <YSpacing height="5px" />
                    <MultiSelectDropDown
                      width="100%"
                      label=""
                      includeSelectAll={true}
                      options={menuOptions}
                      checkedOptions={excludeMenuIds || []}
                      renderCheckedOptions={true}
                      renderCheckedOptionsLabel={true}
                      searchPlaceholder="Search menu cards..."
                      onChange={(selected: string[]) => {
                        setExcludeMenuIds(selected.length > 0 ? selected : null)
                      }}
                    />
                    <div
                      style={{
                        fontSize: '11px',
                        marginTop: '4px',
                        color: '#666',
                      }}
                    >
                      Select specific menu cards to exclude from results
                    </div>
                  </>
                ) : (
                  <>
                    <SectionHeader>Exclude Menu Cards</SectionHeader>
                    <div
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                        color: '#666',
                        padding: '10px 0',
                      }}
                    >
                      Search for menus first to enable this filter
                    </div>
                  </>
                )}
              </FiltersSection>

              <YSpacing height="20px" />
              <Button
                label="Reset Filters"
                width="100%"
                onClick={resetSearch}
              />

              <YSpacing height="20px" />
              <Button
                label="Save Filters"
                width="100%"
                onClick={() => saveFilters(filters)}
              />
            </FiltersPanel>
          )}
          <XSpacing width="20px" />
          <Panel width="100%">
            {!loadingMenus && totalMenus > 0 && (
              <h2>{totalMenus} Menus Found</h2>
            )}
            <YSpacing height="20px" />
            <PlannerTable>
              {loadingMenus ? (
                <LoadingIndicator />
              ) : (
                <tbody>
                  <tr className="table-heading">
                    {tableHeadings
                      .filter((heading) => isColumnVisible(heading))
                      .map((t) => (
                        <th key={t} onClick={() => handleSort(t)}>
                          <ThContent>
                            {t}
                            <SortIcon>
                              {sortFieldMap[t]
                                ? sortByIcon(sortFieldMap[t])
                                : ''}
                            </SortIcon>
                          </ThContent>
                        </th>
                      ))}
                  </tr>
                  {menus.map((menu) => (
                    <tr key={menu.id}>
                      {isColumnVisible('Menu Card Name') &&
                        renderMenuNameWithExcludeButton(menu)}
                      {isColumnVisible('Vendor') && <td>{menu.chef.name}</td>}
                      {isColumnVisible('Menu Card Cutoff Time') &&
                        renderCutoffTime(
                          menu.groupOrderCutoffDays,
                          menu.groupOrderCutoffTime,
                        )}
                      {isColumnVisible('Menu Items') &&
                        renderMenuItemNames(menu.allMenuItems, menu.id)}
                      {isColumnVisible('Cuisine(s)') && (
                        <td>{menu.cuisineTags.join(', ')}</td>
                      )}
                      {isColumnVisible('Ready to Heat') && (
                        <td>
                          {menu.readyToHeat ? (
                            <FaCheckCircle
                              size={20}
                              color={'green'}
                              style={{ marginLeft: '30%' }}
                            />
                          ) : (
                            <FaCircleXmark
                              size={20}
                              color={'red'}
                              style={{ marginLeft: '30%' }}
                            />
                          )}
                        </td>
                      )}
                      {isColumnVisible('Avg Menu Item Price') && (
                        <td>${menu.avgPrice}</td>
                      )}
                      {isColumnVisible('Avg Menu Margin') && (
                        <td>{menu.margin.toFixed(2)}%</td>
                      )}
                      {isColumnVisible('Vendor Pickup Time (On Date)') && (
                        <td>{menu.pickupTime}</td>
                      )}
                      {isColumnVisible('Vendor Earliest Pickup Time') && (
                        <td>
                          {menu.groupOrderEarliestPickupTime &&
                          menu.groupOrderEarliestPickupTime >= 100
                            ? formatNumberToTime(
                                menu.groupOrderEarliestPickupTime,
                              )
                            : ''}
                        </td>
                      )}
                      {isColumnVisible('Vendor Max Items / Day') && (
                        <td>{menu.vendorMaxGOItems}</td>
                      )}
                      {isColumnVisible('Vendor Menucard Cap') && (
                        <td>{menu.menuMaxItems}</td>
                      )}
                      {isColumnVisible('Client Name') && (
                        <td>{menu.clientName}</td>
                      )}
                      {isColumnVisible('Last Ordered by Client') && (
                        <td>{menu.lastOrdered}</td>
                      )}
                      {isColumnVisible('Distance (miles) from Client') && (
                        <td>{menu.distance !== '' ? menu.distance : 'N/A'}</td>
                      )}
                      {isColumnVisible('Avg Client Menu Item Rating') && (
                        <td>{menu.avgMenuItemRatingByClient || 'N/A'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              )}
            </PlannerTable>
          </Panel>
        </FlexContainer>
      </FlexContainer>
    </SchedulePlannerBody>
  )
}

const SchedulePlannerBody = styled.div`
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  min-width: 2800px;

  h1 {
    font-size: 25px;
    margin-bottom: 10px;
    font-family: 'bold';
    color: ${colors.gray400};
  }
`

const PlannerTable = styled.table`
  width: 100%;
  .menu-item-cell {
    min-width: 200px;
    max-width: 300px;
  }
  td {
    border-bottom: 1px solid ${colors.gray200};
    padding: 5px 15px;
    font-family: 'regular';
    font-size: 12px;
    max-width: 200px;
    min-width: 100px;
  }
  th {
    padding: 10px 15px;
    font-family: 'bold';
    text-transform: uppercase;
    font-size: 12px;
    min-width: 100px;
    background: ${colors.gray100};
    cursor: pointer;
    position: sticky;
    top: 0;
    border-bottom: 1px solid ${colors.gray300};
  }
`

const FilterToggle = styled.button`
  display: flex;
  align-items: center;
  font-family: 'regular';
  color: ${colors.blue400};
  margin: ${(props: FilterProps) => props.margin};
  svg {
    margin-right: 5px;
  }
`

interface ExpandBtnProps {
  expanded: boolean | undefined
}

const Expand = styled.button`
  background-color: ${(props: ExpandBtnProps) =>
    props.expanded ? 'lightgrey' : colors.blue200};
  font-weight: bold;
  border-radius: 5px;
  padding: 3px 2px;
`

// Styled components for sorting
const ThContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const SortIcon = styled.span`
  margin-left: 5px;
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${colors.gray400};
`

// Styled components for filters
const FiltersPanel = styled(Panel)`
  padding: 20px;
`

const CheckboxLabel = styled.h3`
  font-family: 'bold';
  font-size: 14px;
`

const SectionHeader = styled.h3`
  font-family: 'bold';
  font-size: 14px;
  margin-bottom: 8px;
`

const SelectionContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;

  input {
    margin-right: 8px;
  }

  label {
    font-size: 12px;
    cursor: pointer;
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 0;
`

// New styled component for filter sections
const FiltersSection = styled.div`
  background-color: ${colors.gray100};
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid ${colors.gray200};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`

const SidebarSectionTitle = styled.h3`
  font-family: 'bold';
  font-size: 16px;
  color: ${colors.violet};
  margin-bottom: 15px;
  display: flex;
  align-items: center;

  &:before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 16px;
    background-color: ${colors.violet};
    margin-right: 8px;
    border-radius: 2px;
  }
`

// Add a styled component for the exclude button
const ExcludeButton = styled.button`
  background: none;
  border: none;
  color: ${colors.gray400};
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.dark500};
    background-color: rgba(255, 0, 0, 0.1);
  }
`

const GroupedMenusButtonStyled = styled.button`
  display: flex;
  align-items: center;
  background-color: ${colors.gray100};
  border: 1px solid ${colors.gray200};
  color: ${colors.violet};
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.gray200};
  }
`

export const ExportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.blue};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.95em;
  transition: background-color 0.2s ease;
  width: 100%;
  text-align: center;

  svg {
    margin-right: 8px;
  }

  &:hover {
    background-color: ${colors.green300};
  }
`

export default GroupOrderMenusFinder
